"use client";

import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShoppingBag, Store, Boxes, Code as Code2, CheckCircle2, ArrowRight, Sparkles, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const platformsData = [{
  id: "shopify",
  name: "Shopify",
  shortName: "Shopify",
  icon: ShoppingBag,
  summary: "Fixed URL structures, collection architecture and tag-based filtering require careful canonical and crawl handling.",
  description: "Shopify includes fixed URL prefixes such as /products/ and /collections/. Hosting defaults provide clean delivery, but limited native control over robots.txt, sitemaps and canonical tags means some optimisations require theme-level code edits or carefully chosen apps. My ecommerce SEO approach covers the same priorities across platforms, including collection page structure, tag duplication and structured data.",
  focusAreas: ["Collection page SEO and category hierarchy planning", "Managing tag-based filter duplication and canonical tags", "Theme-level technical fixes and crawl path adjustments", "Selective app audits to prevent script bloat and code conflicts", "Product, offer and breadcrumb structured data configuration"]
}, {
  id: "woocommerce",
  name: "WooCommerce",
  shortName: "WooCommerce",
  icon: Store,
  summary: "Complete flexibility across URLs, taxonomies and metadata, balanced against hosting quality and plugin hygiene.",
  description: "WooCommerce gives full control over URL structures, permalinks, robots.txt, canonical tags and metadata through its plugin ecosystem. That flexibility requires governance: weak hosting, theme bloat or conflicting plugins can slow pages down and generate invalid schema or duplicate metadata. I audit server performance, clean taxonomies and keep plugin load lean.",
  focusAreas: ["Clean permalink and taxonomy structure (category vs product-category)", "Crawlable parameter URL and faceted navigation management", "Plugin hygiene audits to eliminate conflicting metadata and bloated scripts", "Hosting environment and server response assessment", "Valid product, review and breadcrumb schema implementation"]
}, {
  id: "magento-bigcommerce",
  name: "Magento & BigCommerce",
  shortName: "Magento & BigCommerce",
  icon: Boxes,
  summary: "High-SKU catalogues, multi-storefront views and layered navigation requiring strict parameter controls.",
  description: "Magento (Adobe Commerce) and BigCommerce typically power larger catalogues with thousands of SKUs, multi-storefront views, regional versions and language variants. They offer strong architectural control but require disciplined crawl management. Layered navigation in Magento often generates filter-combination URL explosions that need parameter handling, canonicalisation or blocking.",
  focusAreas: ["Layered navigation and multi-filter URL parameter management", "Crawl budget protection across large SKU counts and category facets", "Canonicalisation and hreflang setup across multi-region storefronts", "Segmented XML sitemaps separating canonical products from facets", "Developer-ready specifications for backend and frontend engineering teams"]
}, {
  id: "custom-headless",
  name: "Custom or Headless",
  shortName: "Custom or headless",
  icon: Code2,
  summary: "Auditing JavaScript rendering, crawl paths and API-driven metadata to ensure search engines see your catalogue.",
  description: "Headless builds and custom ecommerce architectures decouple the frontend from the commerce engine. While flexible, JavaScript-heavy themes or client-side rendering can stop search engine crawlers from indexing product descriptions, variants and category grids. I audit what crawlers can actually access and deliver clear technical recommendations for your developers.",
  focusAreas: ["JavaScript rendering verification and server-side rendering (SSR) crawl checks", "Ensuring crawl paths reach every canonical product and collection", "API-driven structured data validation and metadata hydration", "Clean URL routing and canonical tag alignment across headless endpoints", "Implementation-ready briefs for in-house and agency engineering teams"]
}];
export default function Platforms() {
  const [activeTab, setActiveTab] = useState("shopify");
  const shouldReduceMotion = useReducedMotion();
  const activePlatform = platformsData.find(p => p.id === activeTab) || platformsData[0];
  return <section id="ecommerceplatforms" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 border-b border-border transition-colors duration-200">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Heading */}
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/30 text-primary font-medium rounded-full">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            Platform Adaptability
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground">
            Shopify, WooCommerce and other ecommerce platforms
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Every ecommerce platform has distinct technical constraints, indexing mechanics and template structures.
            I adapt to the stack you run rather than forcing one rigid methodology, focusing on what moves commercial visibility.
          </p>

          <p className="mt-3 text-xs sm:text-sm text-muted-foreground italic">
            I work within each system's capabilities and do not claim formal platform certifications.
          </p>
        </div>

        {/* Platform Selector Tabs */}
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-10">
          {platformsData.map((platform, i) => {
          const Icon = platform.icon;
          const isActive = platform.id === activeTab;
          return <Button key={platform.id} data-index={i} type="button" aria-pressed={isActive} variant={isActive ? "default" : "outline"} size="sm" onClick={() => setActiveTab(platform.id)} className={`rounded-full px-4 sm:px-5 transition-all text-xs sm:text-sm font-medium flex items-center gap-2 ${isActive ? "bg-primary text-primary-foreground shadow-xs" : "text-muted-foreground hover:text-foreground border-border bg-card"}`}>
                <Icon className="size-4 shrink-0" />
                <span>{platform.shortName}</span>
              </Button>;
        })}
        </div>

        {/* Active Platform Detail Card */}
        <div className="max-w-5xl mx-auto">
          <motion.div key={activePlatform.id} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3
        }}>
            <Card className="bg-card text-card-foreground border-border shadow-xs rounded-xl overflow-hidden p-6 sm:p-8 lg:p-10">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
                {/* Left Overview Column */}
                <div className="lg:col-span-5 space-y-5">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-lg bg-primary/10 text-primary">
                      <activePlatform.icon className="size-6 text-primary" />
                    </div>
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                        Platform Architecture
                      </span>
                      <h3 className="text-xl sm:text-2xl font-semibold font-default text-foreground">
                        {activePlatform.name}
                      </h3>
                    </div>
                  </div>

                  <p className="text-sm font-medium text-foreground leading-snug">
                    {activePlatform.summary}
                  </p>

                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {activePlatform.description}
                  </p>

                  <div className="pt-4 border-t border-border">
                    <div className="flex items-start gap-2.5 text-xs text-muted-foreground">
                      <Info className="size-4 text-primary shrink-0 mt-0.5" />
                      <span>
                        Recommendations are provided as implementation-ready tickets for your developers, or made directly inside the platform when access is granted.
                      </span>
                    </div>
                  </div>
                </div>

                {/* Right Focus Areas Column */}
                <div className="lg:col-span-7 bg-muted/40 p-6 sm:p-7 rounded-lg border border-border">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                    Key platform SEO considerations
                  </h4>

                  <ul className="space-y-3">
                    {activePlatform.focusAreas.map((item, idx) => <li key={idx} data-index={idx} className="flex items-start gap-3">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-1" />
                        <span className="text-sm text-foreground leading-normal">
                          {item}
                        </span>
                      </li>)}
                  </ul>

                  <div className="mt-8 pt-6 border-t border-border/80 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="text-xs text-muted-foreground text-center sm:text-left">
                      Have questions about your {activePlatform.shortName} store's setup?
                    </p>
                    <Button asChild size="sm" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 rounded-md">
                      <Link to="/ContactPage">
                        <span>Book a Free SEO Consultation</span>
                        <ArrowRight className="size-3.5 ml-1.5" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Closing Action Row */}
        <div className="mt-14 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
          <Button asChild size="default" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md">
            <Link to="/ContactPage">
              Book a Free SEO Consultation
            </Link>
          </Button>
          <Button asChild size="default" variant="outline" className="border-border text-foreground hover:bg-muted rounded-md">
            <Link to="/ContactPage?topic=free-seo-audit">
              Get a Free SEO Audit
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}